// The season timelapse players: the view model (ui-map-interface.md §3.6,
// chronicle.md §10, R1620).
//
// Three places play the owner frames of a season: the season summary card
// (card 07 of mockup round 076), WAR · SOVEREIGNTY (card 09) and the public
// reader (card 10). This module holds what the three share and what a test
// reads with no DOM: the stamp of a frame, the cell count of each faction
// at a frame, the legend, the day marks under a scrubber and the frame a
// chronicle day page points at.
//
// The game keeps no owner of a cell finer than the faction (R1594), so a
// legend marks the reader's own FACTION and lifts no cell of an empire
// (R1620).

import { NUM_FACTIONS } from '../../gen/balance';
import { timelapse as T } from '../../gen/strings';
import { TIMELAPSE_RIDGE, type Timelapse } from '../../net/timelapse';
import { factionColor, factionName, hex } from '../../world';
import { esc } from '../esc';
import { groupThousands } from '../../locale/group';
import { hhmm, SEC_PER_DAY, SEC_PER_HOUR } from '../../locale/clock';

/**
 * The season day of a world second, counted from 1. Day 1 is the world
 * seconds 0 to 86 400 of the season, as the chronicle counts its day
 * pages (chronicle.md §6.3, R1177), so ▸ ON THE MAP lands on a frame that
 * names the day of its page.
 */
export function seasonDay(tSec: number): number {
  return Math.floor(Math.max(0, tSec) / SEC_PER_DAY) + 1;
}

/** The stamp of one owner frame: "DAY 3 · 06:00". */
export function frameStamp(tSec: number): string {
  return T.stamp({ day: seasonDay(tSec), time: hhmm(Math.max(0, tSec)) });
}

/**
 * The cells each owner holds in one frame, indexed by the owner code: 0 is
 * unclaimed water, 1 to `factions` a faction slot. A ridge cell counts for
 * no owner (R105), and neither does a code past the faction count.
 */
export function frameCounts(cells: Uint8Array, factions = NUM_FACTIONS): number[] {
  const out = new Array<number>(factions + 1).fill(0);
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    if (c <= factions) out[c]++;
  }
  return out;
}

/**
 * The owner plane of the live map for one frame: the frame with every
 * ridge cell unowned, because the live plane holds 0 for rock. The map
 * layers draw it in the place of the live owners.
 */
export function ownersOf(cells: Uint8Array, out?: Uint8Array): Uint8Array {
  const o = out && out.length === cells.length ? out : new Uint8Array(cells.length);
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    o[i] = c === TIMELAPSE_RIDGE ? 0 : c;
  }
  return o;
}

/** One faction row of a legend. */
export interface LegendRow {
  faction: number;
  name: string;
  color: string;
  count: number;
  /** The reader's own faction, or the volume commander's (R1620). */
  mine: boolean;
}

/**
 * The legend of one frame: every faction, the largest holder first, and a
 * tie in faction order, so two equal counts never swap places between two
 * frames. Unclaimed water has no row: the map shows it as water.
 */
export function legendRows(counts: readonly number[], mine: number): LegendRow[] {
  const out: LegendRow[] = [];
  for (let f = 1; f < counts.length; f++) {
    out.push({ faction: f, name: factionName(f), color: hex(factionColor(f)), count: counts[f], mine: f === mine });
  }
  out.sort((a, b) => b.count - a.count || a.faction - b.faction);
  return out;
}

/**
 * The legend as markup: a swatch, the name and the count of each faction.
 * The marked row carries the white outline of the mockup's YOUR CELLS
 * swatch and a title that names the mark, so the mark reads by shape and
 * by word and never by colour alone.
 */
export function legendHtml(rows: readonly LegendRow[], markTitle: string): string {
  return rows.map((r) => {
    const title = r.mine ? ` title="${esc(markTitle)}"` : '';
    return `<i class="sw${r.mine ? ' me' : ''}" style="background:${r.color}"${title}></i>`
      + `<span class="n${r.mine ? ' me' : ''}"${title}>${esc(r.name)}</span>`
      + `<span class="c">${groupThousands(r.count)}</span>`;
  }).join('');
}

/**
 * The frame a chronicle day page points at: the first frame at or after
 * the start of that day. A day past the last frame points at the last
 * frame, and a timelapse with no frame answers -1.
 */
export function dayFrame(tl: Timelapse, day: number): number {
  const n = tl.frames.length;
  if (n === 0) return -1;
  const from = (Math.max(1, day) - 1) * SEC_PER_DAY;
  for (let i = 0; i < n; i++) if (tl.frames[i].tSec >= from) return i;
  return n - 1;
}

/**
 * The day marks under a scrubber: at most `n` marks spread evenly from the
 * day of the first frame to the day of the last. The first mark names the
 * day, the others state the number alone. `now` turns the last mark into
 * NOW. A day stands once: a span of fewer days than marks states each day
 * one time, so day 1 of a season reads "DAY 1 · NOW" and not
 * "DAY 1 · 1 · 1 · NOW".
 */
export function dayTicks(firstSec: number, lastSec: number, n: number, now = false): string[] {
  const a = seasonDay(firstSec);
  const b = seasonDay(lastSec);
  const out: string[] = [];
  let last = -1;
  for (let i = 0; i < n; i++) {
    if (now && i === n - 1) { out.push(T.tick_now); break; }
    const day = n > 1 ? Math.round(a + ((b - a) * i) / (n - 1)) : a;
    if (day === last) continue;
    last = day;
    out.push(i === 0 ? T.tick_day({ day }) : String(day));
  }
  return out;
}

/** The marks as markup. */
export function ticksHtml(ticks: readonly string[]): string {
  return `<div class="tlticks">${ticks.map((t) => `<span>${esc(t)}</span>`).join('')}</div>`;
}

/**
 * The frame a season opens on: the newest frame in which a faction owns a
 * cell. The settlement of stage 3 drains every cell before the close takes
 * the last frame (numbers.md §15), so the last frame of a closed season is
 * an empty sea; the frame before the drain is the sea the season ended
 * with. A season in which no frame holds an owned cell opens on its last
 * frame, and a timelapse with no frame answers -1.
 */
export function openingFrame(tl: Timelapse, factions = NUM_FACTIONS): number {
  for (let k = tl.frames.length - 1; k >= 0; k--) {
    const cells = tl.frames[k].cells;
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (c >= 1 && c <= factions) return k;
    }
  }
  return tl.frames.length - 1;
}

/** The cadence of the newest frames in whole hours, at least 1. */
export function everyHours(tl: Timelapse): number {
  return Math.max(1, Math.round(tl.everySec / SEC_PER_HOUR));
}

/** True when a timelapse fits the grid of the live map. A frame of another
 *  map size cannot stand in for the live owners. */
export function fitsGrid(tl: Timelapse | null, cols: number, rows: number): tl is Timelapse {
  return tl !== null && tl.frames.length > 0 && tl.cols === cols && tl.rows === rows;
}
